/**
 * Staff/Impersonation mode utilities.
 *
 * When `REACT_APP_ENV === 'staff'`, the admin is impersonating a real user.
 * In this mode:
 *  - The LLM streaming API (`/chat_stream`) still works (it doesn't persist data).
 *  - All persistence APIs (create/update message, create conversation, feedback, etc.) are SKIPPED.
 *  - Chat state lives only in Redux; a page refresh clears everything.
 *  - Feedback buttons (thumbs-up/down, copy, speak) are disabled.
 *  - Conversation IDs are generated locally (dummy IDs).
 */

import { UserProfile } from '@/interface/profile';

export const isStaffMode = (): boolean => {
  return process.env.REACT_APP_ENV === 'staff';
};

/**
 * Generate a local-only dummy conversation ID for staff/impersonation mode.
 * These IDs are never sent to the backend.
 */
export const generateStaffConversationId = (): string => {
  return `staff-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
};

/**
 * Check whether a conversation ID was generated locally in staff mode.
 * Useful to skip backend calls that would 404 for non-existent IDs.
 */
export const isStaffConversationId = (id?: string): boolean => {
  return !!id && id.startsWith('staff-');
};

/**
 * Generate a local-only dummy message ID for staff/impersonation mode.
 */
export const generateStaffMessageId = (): string => {
  return `staff-msg-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
};

const STAFF_PROFILE_KEY = 'profileStaff';

/**
 * Shape version of the cached impersonated profile.
 *
 * Bump this whenever a field is added to `mapAgentToUserProfile`. A cached blob
 * from an older build rehydrates verbatim, so a profile missing newly-added
 * fields would silently degrade the `/chat_stream` payload (e.g.
 * `memberkeynumeric: 0`) with no error. Bumping invalidates those blobs and
 * forces a clean re-impersonation instead.
 *
 * v1 = unversioned legacy blobs, which are always rejected.
 */
export const STAFF_PROFILE_SCHEMA_VERSION = 2;

interface StoredStaffProfile {
  schemaVersion: number;
  profile: UserProfile;
}

/** Cache the impersonated profile, stamped with the current schema version. */
export const writeStaffProfile = (profile: UserProfile): void => {
  const envelope: StoredStaffProfile = {
    schemaVersion: STAFF_PROFILE_SCHEMA_VERSION,
    profile,
  };
  localStorage.setItem(STAFF_PROFILE_KEY, JSON.stringify(envelope));
};

/**
 * Read the cached impersonated profile.
 *
 * Returns `null` for anything this build cannot trust — absent, unparseable,
 * unversioned, or stamped with a different schema version. Callers treat `null`
 * as "not impersonating", which routes the admin back to `/staff`.
 */
export const readStaffProfile = (): UserProfile | null => {
  try {
    const raw = localStorage.getItem(STAFF_PROFILE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as Partial<StoredStaffProfile>;
    if (stored?.schemaVersion !== STAFF_PROFILE_SCHEMA_VERSION) return null;
    return stored.profile ?? null;
  } catch (e) {
    console.error('Invalid stored staff profile:', e);
    return null;
  }
};

/** Whether a trustworthy impersonated profile is cached. */
export const hasStaffProfile = (): boolean => readStaffProfile() !== null;
